import { useNavigate } from 'react-router';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { toFlagTargeting } from '@gonfalon/navigator/navigators';
import { snackbarQueue } from '@launchpad-ui/components';
import { useMutation } from '@tanstack/react-query';

import {
  OnboardingFlowGroup,
  type OnboardingGuidanceFlow,
  type OnboardingGuidanceFlowWithHandler,
  type OnboardingGuidanceStep,
} from '../../../types';
import { useCreateIndividualTargetingFlag } from '../../useCreateIndividualTargetingFlag';

export const stepOne: OnboardingGuidanceStep = {
  title: 'Add this flag to your app',
  description: 'Choose a small change in your app. Wrap the change with this code snippet to control how it ships.',
  selector: 'install-flag-in-code',
};

export const stepTwo: OnboardingGuidanceStep = {
  title: 'Toggle on your flag',
  description:
    "This tells LaunchDarkly to evaluate targeting rules for the flag's **On** state until it finds a match. You'll review and save before this change takes effect.",
  selector: 'flip-toggle-to-on',
};

export const stepThree: OnboardingGuidanceStep = {
  title: 'Define targeting rules',
  description:
    'You can define targeting rules to control who or what is impacted by a change. For this rule, only you see the change. The default rule shows the original experience with no changes.',
  selector: 'verify-flag-is-targeting-you',
};

export const stepFour: OnboardingGuidanceStep = {
  title: 'Save your changes',
  description: 'Saving applies this change. Select "Review and save" to proceed to confirmation.',
  selector: 'review-and-save',
};

export const stepFive: OnboardingGuidanceStep = {
  title: 'Confirm save',
  description: 'Review your changes, then confirm to commit.',
  selector: 'confirm-save',
};

const flagTargetingDocsUrl = getDocumentationUrl('home/flags/target');

export const stepSix: OnboardingGuidanceStep = {
  title: 'Check the change in your app',
  description: `Your change was released. It should now be visible in your app. To learn more about flag targeting, [read the docs](${flagTargetingDocsUrl}).`,
  // No selector: step binds to center of screen (no arrow, no Back/Continue/Done).
};

const ONBOARDING_FLOW_NAME = 'individualTargetRule';
const INITIAL_STEP_INDEX = 0;

export const individualTargetRule: OnboardingGuidanceFlow = {
  name: 'Target individual users',
  description: 'Learn the basics of feature flags with simple targeting rules.',
  steps: [stepOne, stepTwo, stepThree, stepFour, stepFive, stepSix],
  group: OnboardingFlowGroup.FeatureFlags,
};

/**
 * Hook that returns the individualTargetRule flow with its onClick handler configured,
 * along with loading and error state.
 * This hook should be used in components that need to display this flow.
 */
export function useIndividualTargetRule(onClose?: () => void) {
  const navigate = useNavigate();
  const { context, selectedEnvironment } = useProjectContext();
  const { createFlag } = useCreateIndividualTargetingFlag();

  const [completedValue] = useStorageSlot(ONBOARDING_FLOW_NAME);
  const isCompleted = completedValue === 'true';

  const {
    mutate: handleCreateFlag,
    isPending,
    isSuccess,
    error,
  } = useMutation({
    mutationFn: async () => {
      if (!context?.projectKey || !selectedEnvironment?.key) {
        throw new Error('Project or environment context is not available');
      }

      const createdFlag = await createFlag();

      // Get the full Path object from toFlagTargeting (includes selected-env and other params)
      const flagTargetingPath = toFlagTargeting({
        projectKey: context.projectKey,
        flagKey: createdFlag.key,
        environmentKey: selectedEnvironment.key,
      });

      // Merge onboarding query parameters with existing search params from toFlagTargeting
      const mergedSearchParams = new URLSearchParams(flagTargetingPath.search);
      mergedSearchParams.set('onboardingFlow', ONBOARDING_FLOW_NAME);
      mergedSearchParams.set('step', String(INITIAL_STEP_INDEX));

      // Navigate to the flag targeting page with merged query params
      void navigate(
        {
          ...flagTargetingPath,
          search: mergedSearchParams.toString(),
        },
        { replace: true },
      );

      // Close the drawer/modal after successful navigation
      onClose?.();
    },
    onError: (err) => {
      snackbarQueue.add({
        description: err instanceof Error ? err.message : 'Failed to create flag. Please try again.',
        status: 'error',
      });
    },
  });

  return {
    flow: {
      ...individualTargetRule,
      completed: isCompleted,
      onClick: () => handleCreateFlag(),
    } as OnboardingGuidanceFlowWithHandler,
    isCreating: isPending || isSuccess,
    error,
  };
}
